import API
  from "../../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| API
|--------------------------------------------------------------------------
*/

const api =
  new API();


/*
|--------------------------------------------------------------------------
| NORMALIZE
|--------------------------------------------------------------------------
*/

const normalizeString = (
  value
) =>
  String(
    value || ""
  ).trim();


/*
|--------------------------------------------------------------------------
| GET EXTENSION
|--------------------------------------------------------------------------
*/

const getExtension = (
  reportType,
  format
) => {
  const normalizedType =
    normalizeString(
      reportType
    ).toUpperCase();

  const normalizedFormat =
    normalizeString(
      format
    ).toLowerCase();


  /*
  |--------------------------------------------------------------------------
  | CSV
  |--------------------------------------------------------------------------
  */

  if (
    normalizedFormat ===
    "csv"
  ) {
    return "csv";
  }


  /*
  |--------------------------------------------------------------------------
  | JSON
  |--------------------------------------------------------------------------
  */

  if (
    normalizedFormat ===
    "json"
  ) {
    return "json";
  }


  /*
  |--------------------------------------------------------------------------
  | DEFAULTS
  |--------------------------------------------------------------------------
  */

  if (
    normalizedType ===
    "HSN" ||
    normalizedType ===
    "RECONCILIATION"
  ) {
    return "csv";
  }


  return "json";
};


/*
|--------------------------------------------------------------------------
| GET ACCEPT TYPE
|--------------------------------------------------------------------------
*/

const getAcceptType = (
  format
) => {
  const normalizedFormat =
    normalizeString(
      format
    ).toLowerCase();


  if (
    normalizedFormat ===
    "csv"
  ) {
    return "text/csv";
  }


  if (
    normalizedFormat ===
    "json"
  ) {
    return "application/json";
  }


  return "*/*";
};


/*
|--------------------------------------------------------------------------
| BUILD FALLBACK FILE NAME
|--------------------------------------------------------------------------
*/

const buildFileName = ({
  reportType,
  returnPeriod,
  format,
}) => {
  const extension =
    getExtension(
      reportType,
      format
    );


  const type =
    normalizeString(
      reportType
    ).toUpperCase() ||
    "GST";


  const period =
    normalizeString(
      returnPeriod
    ) ||
    "gst";


  return `${type}-${period}.${extension}`;
};


/*
|--------------------------------------------------------------------------
| VALIDATE EXPORT PARAMS
|--------------------------------------------------------------------------
*/

const validateExportParams = ({
  reportType,
  format,
  fromDate,
  toDate,
}) => {
  if (
    !reportType
  ) {
    return {
      valid:
        false,

      message:
        "GST report type is required.",
    };
  }


  if (
    !fromDate
  ) {
    return {
      valid:
        false,

      message:
        "GST From Date is required.",
    };
  }


  if (
    !toDate
  ) {
    return {
      valid:
        false,

      message:
        "GST To Date is required.",
    };
  }


  const supportedFormats = [
    "json",
    "csv",
  ];


  if (
    format &&
    !supportedFormats.includes(
      normalizeString(
        format
      ).toLowerCase()
    )
  ) {
    return {
      valid:
        false,

      message:
        "Unsupported GST export format.",
    };
  }


  return {
    valid:
      true,
  };
};


/*
|--------------------------------------------------------------------------
| DOWNLOAD GST REPORT
|--------------------------------------------------------------------------
|
| Example:
|
| downloadGstReport({
|   reportType: "GSTR1",
|   format: "csv",
|   fromDate: "2026-08-01",
|   toDate: "2026-08-08",
|   financialYear: "2026-27",
|   gstin: "...",
|   returnPeriod: "082026",
| });
|
|--------------------------------------------------------------------------
*/

export const downloadGstReport =
  async ({
    reportType,

    format,

    fromDate,

    toDate,

    financialYear,

    gstin,

    returnPeriod,

    force = false,
  }) => {
    /*
    |--------------------------------------------------------------------------
    | NORMALIZE
    |--------------------------------------------------------------------------
    */

    const normalizedReportType =
      normalizeString(
        reportType
      ).toUpperCase();


    const normalizedFormat =
      normalizeString(
        format
      ).toLowerCase();


    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    const validation =
      validateExportParams({
        reportType:
          normalizedReportType,

        format:
          normalizedFormat,

        fromDate,

        toDate,
      });


    if (
      !validation.valid
    ) {
      return {
        success:
          false,

        status:
          "error",

        message:
          validation.message,
      };
    }


    /*
    |--------------------------------------------------------------------------
    | EXPORT URL
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | config.exportGstReport() should use exactly the SAME backend base URL
    | style as the rest of your config methods.
    |
    | Example:
    |
    | http://localhost:9004/v1/admin/gst/export/GSTR1
    |
    |--------------------------------------------------------------------------
    */

    const url =
      config.exportGstReport(
        normalizedReportType
      );


    /*
    |--------------------------------------------------------------------------
    | PARAMS
    |--------------------------------------------------------------------------
    */

    const params = {
      format:
        normalizedFormat,

      fromDate,

      toDate,

      financialYear,

      gstin:
        normalizeString(
          gstin
        ),

      returnPeriod:
        normalizeString(
          returnPeriod
        ),

      force:
        force
          ? "true"
          : undefined,
    };


    /*
    |--------------------------------------------------------------------------
    | FALLBACK FILE NAME
    |--------------------------------------------------------------------------
    */

    const fileName =
      buildFileName({
        reportType:
          normalizedReportType,

        returnPeriod,

        format:
          normalizedFormat,
      });


    /*
    |--------------------------------------------------------------------------
    | ACCEPT
    |--------------------------------------------------------------------------
    */

    const accept =
      getAcceptType(
        normalizedFormat
      );


    /*
    |--------------------------------------------------------------------------
    | DOWNLOAD THROUGH API HELPER
    |--------------------------------------------------------------------------
    |
    | NO direct fetch() here.
    |
    | Benefits:
    |
    | - Uses accessToken
    | - Uses same API URL strategy
    | - Same error handling
    | - Blob handling
    | - Content-Disposition handling
    |--------------------------------------------------------------------------
    */

const response =
  await api.downloadAndSave(
    url,
    {
      params,

      authentication:
        true,

      tokenKey:
        "accessToken",

      headers: {
        Accept:
          accept,
      },
    }
  );


    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    if (
      !response?.success
    ) {
      console.error(
        "GST export failed:",
        {
          reportType:
            normalizedReportType,

          format:
            normalizedFormat,

          response,
        }
      );


      return {
        success:
          false,

        status:
          "error",

        code:
          response?.code ||
          0,

        message:
          response?.message ||
          "Unable to export GST report.",

        errorCode:
          response?.errorCode ||
          null,

        details:
          response?.details ||
          null,

        validation:
          response?.validation ||
          null,
      };
    }


    /*
    |--------------------------------------------------------------------------
    | SUCCESS
    |--------------------------------------------------------------------------
    */


    return {
      success:
        true,

      status:
        "success",

      fileName:
        response.fileName,

      contentType:
        response.contentType,

      size:
        response.size,
    };
  };


/*
|--------------------------------------------------------------------------
| EXPORT GSTR-1 JSON
|--------------------------------------------------------------------------
*/

export const downloadGstr1Json =
  async (
    params = {}
  ) =>
    downloadGstReport({
      ...params,

      reportType:
        "GSTR1",

      format:
        "json",
    });


/*
|--------------------------------------------------------------------------
| EXPORT GSTR-1 CSV
|--------------------------------------------------------------------------
*/

export const downloadGstr1Csv =
  async (
    params = {}
  ) =>
    downloadGstReport({
      ...params,

      reportType:
        "GSTR1",

      format:
        "csv",
    });


/*
|--------------------------------------------------------------------------
| EXPORT GSTR-3B
|--------------------------------------------------------------------------
*/

export const downloadGstr3bJson =
  async (
    params = {}
  ) =>
    downloadGstReport({
      ...params,

      reportType:
        "GSTR3B",

      format:
        "json",
    });


/*
|--------------------------------------------------------------------------
| EXPORT HSN CSV
|--------------------------------------------------------------------------
*/

export const downloadHsnCsv =
  async (
    params = {}
  ) =>
    downloadGstReport({
      ...params,

      reportType:
        "HSN",

      format:
        "csv",
    });


/*
|--------------------------------------------------------------------------
| EXPORT RECONCILIATION CSV
|--------------------------------------------------------------------------
*/

export const downloadReconciliationCsv =
  async (
    params = {}
  ) =>
    downloadGstReport({
      ...params,

      reportType:
        "RECONCILIATION",

      format:
        "csv",
    });


/*
|--------------------------------------------------------------------------
| DEFAULT
|--------------------------------------------------------------------------
*/

export default {
  downloadGstReport,

  downloadGstr1Json,

  downloadGstr1Csv,

  downloadGstr3bJson,

  downloadHsnCsv,

  downloadReconciliationCsv,
};